import React, { useEffect, useRef, useState } from 'react';
import { ChatbotMessage } from '../types/wizard.types';
import { cn } from '@/shared/lib/utils';
import { useLanguageDirection } from '@/shared/hooks';
import { Button } from '@/shared/components/ui/Button';
import { ChevronLeft, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface WizardChatbotContainerProps {
  messages?: ChatbotMessage[];
  currentInput?: React.ReactNode;
  groupTitle?: string;
  className?: string;
  scrollToBottomRef?: React.MutableRefObject<(() => void) | null>;
  onPrevious?: () => void;
  canGoBack?: boolean;
  onExit: () => void;
}

// Typing indicator component - modern minimalistic design
const TypingIndicator: React.FC = () => (
  <div className="flex justify-start animate-in fade-in duration-300">
    <div className="bg-white border border-gray-100 text-gray-700 rounded-2xl rounded-bl-sm shadow-sm px-4 py-3 max-w-[80%]">
      <div className="flex items-center space-x-1">
        <div
          className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: '0ms', animationDuration: '1s' }}
        />
        <div
          className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: '150ms', animationDuration: '1s' }}
        />
        <div
          className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
          style={{ animationDelay: '300ms', animationDuration: '1s' }}
        />
      </div>
    </div>
  </div>
);

export const WizardChatbotContainer: React.FC<WizardChatbotContainerProps> = ({
  messages,
  currentInput,
  groupTitle,
  className = '',
  scrollToBottomRef,
  onPrevious,
  canGoBack = false,
  onExit,
}) => {
  const { isRTL } = useLanguageDirection();
  const { t } = useTranslation('wizard');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(false);

  // Function to scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Expose scroll function to parent
  useEffect(() => {
    if (scrollToBottomRef) {
      scrollToBottomRef.current = scrollToBottom;
    }
  }, [scrollToBottomRef]);

  // Show typing indicator when last message is from bot
  useEffect(() => {
    if (messages && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      if (lastMessage && lastMessage.type === 'bot') {
        setIsTyping(true);

        // Hide typing indicator after a delay
        const timer = setTimeout(() => {
          setIsTyping(false);
        }, 700);

        return () => clearTimeout(timer);
      }
    }
    return undefined;
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive or typing indicator changes
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus on input after typing indicator disappears
  useEffect(() => {
    if (!isTyping && currentInput) {
      // Small delay to ensure the UI has updated
      const focusTimer = setTimeout(() => {
        const inputElement = inputContainerRef.current?.querySelector(
          'input, textarea, button[type="submit"]'
        ) as HTMLElement;
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);

      return () => clearTimeout(focusTimer);
    }
    return undefined;
  }, [isTyping, currentInput]);

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-gradient-to-b from-gray-50/50 to-white',
        className
      )}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Header with group title and exit button */}
      <div className="bg-white border-b border-muted-foreground/10 px-6 py-4 shadow-sm flex justify-between items-center">
        {groupTitle && <h2 className="text-lg font-semibold">{groupTitle}</h2>}
        <Button
          variant="ghost"
          size="icon"
          onClick={onExit}
          className="h-8 w-8 rounded-full hover:bg-accent/50 transition-all duration-200 z-10 pointer-events-auto"
          aria-label={t('common.exit.exitWizard')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Messages container - clean and spacious */}
      <div className="flex-1 overflow-y-auto px-6 py-8 space-y-4">
        {messages?.map((message, index) => {
          // Don't show the last message if it's a bot message and we're showing typing indicator
          const isLastMessage = index === messages.length - 1;
          const shouldHideLastBotMessage =
            isLastMessage && message.type === 'bot' && isTyping;

          if (shouldHideLastBotMessage) {
            return null;
          }

          return (
            <div
              key={message.id}
              className={cn(
                'flex animate-in fade-in slide-in-from-bottom-2 duration-300',
                message.type === 'user' ? 'justify-end' : 'justify-start'
              )}
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div
                className={cn(
                  'max-w-3/4 lg:max-w-3/5 px-4 py-3 shadow-sm',
                  message.type === 'user'
                    ? 'bg-blue-600 text-white rounded-2xl rounded-br-sm'
                    : 'bg-white border border-gray-100 text-gray-800 rounded-2xl rounded-bl-sm'
                )}
              >
                <p className="text-sm leading-relaxed whitespace-pre-wrap overflow-wrap-anywhere break-words">
                  {message.content}
                </p>
              </div>
            </div>
          );
        })}

        {/* Typing indicator for bot messages */}
        {isTyping && <TypingIndicator />}

        <div ref={messagesEndRef} />
      </div>

      {/* Current input area */}
      {currentInput && (
        <div className="bg-white border-t border-gray-100 shadow-sm">
          {/* Input container */}
          <div ref={inputContainerRef} className="px-6 py-4">
            {currentInput}
          </div>

          {/* Navigation footer — Previous always at the bottom */}
          {canGoBack && onPrevious && (
            <div className="px-6 pb-4 pt-0">
              <Button
                variant="ghost"
                size="sm"
                onClick={onPrevious}
                className="text-muted-foreground hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4 me-1 rtl:rotate-180" />
                {t('common.buttons.previous')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
